import { useCallback, useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { CustomerResponse, CustomerPrintSettings } from "../../../types/customer.type";
import { getCustomerPrintSettings } from "../../../services";
import { featureAccess } from "../../../permissions/access";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import PrintSettingsModal from "./PrintSettingsModal";
import { getCopyTypes } from "./printCopyTypes";

interface PrintSettingsSummaryProps {
  customerDetails: CustomerResponse;
}

const setTotal = (copies: { quantity: number }[]) =>
  copies.reduce((total, c) => total + c.quantity, 0);

export default function PrintSettingsSummary({
  customerDetails,
}: PrintSettingsSummaryProps) {
  const role = useUserRole();
  const canManage = featureAccess.printSettings(role);

  const customerUuid = customerDetails.uuid;
  const isEtaxShop = customerDetails.etax_enabled === true;
  const copyTypes = getCopyTypes(isEtaxShop);
  const [settings, setSettings] = useState<CustomerPrintSettings | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadSettings = useCallback(async () => {
    const {
      data: { data },
    } = await getCustomerPrintSettings(customerUuid);
    setSettings(data);
  }, [customerUuid]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const sets = settings?.sets ?? [];

  return (
    <Paper sx={{ padding: "16px 24px", mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          การตั้งค่าการพิมพ์
        </Typography>
        {sets.length > 0 && (
          <Chip
            label={`${sets.length} ชุด`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {settings?.is_default && sets.length > 0 && (
          <Chip label="ค่าเริ่มต้น" size="small" variant="outlined" />
        )}
        {canManage && (
          <Button
            variant="contained"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => setModalOpen(true)}
            sx={{ ml: "auto" }}
          >
            ตั้งค่าการพิมพ์
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        ชุดการพิมพ์ที่กำหนดไว้สำหรับลูกค้ารายนี้ —
        พนักงานขายจะเลือกชุดก่อนพิมพ์บิลจากแอป
      </Typography>

      <Box sx={{ mt: 2 }}>
        {sets.length === 0 ? (
          <Box
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              p: 3.5,
              textAlign: "center",
              color: "text.disabled",
            }}
          >
            <Typography variant="body2">
              ยังไม่ได้ตั้งค่าการพิมพ์ — กด «ตั้งค่าการพิมพ์» เพื่อเพิ่มชุด
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {sets.map((set, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={`${set.name}-${index}`}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="body1" fontWeight={700}>
                      {set.name}
                    </Typography>
                    <Chip
                      label={`${setTotal(set.copies)} ใบ`}
                      size="small"
                      sx={{
                        bgcolor: "#FFF5E7",
                        color: "#8A5A00",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {copyTypes.map((t) => {
                      const copy = set.copies.find((c) => c.type === t.key);
                      const active = Boolean(copy);
                      return (
                        <Chip
                          key={t.key}
                          size="small"
                          variant="outlined"
                          color={active ? "primary" : "default"}
                          sx={{ opacity: active ? 1 : 0.5 }}
                          label={
                            <Box
                              component="span"
                              sx={{ display: "inline-flex", gap: 0.5 }}
                            >
                              <Box component="span" sx={{ fontWeight: 700 }}>
                                {t.key}
                              </Box>
                              <Box component="span" sx={{ opacity: 0.75 }}>
                                {t.short}
                              </Box>
                              <Box component="span" sx={{ fontWeight: 700 }}>
                                ×{copy?.quantity ?? 0}
                              </Box>
                            </Box>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {canManage && (
        <PrintSettingsModal
          open={modalOpen}
          customerUuid={customerUuid}
          customerName={customerDetails.name}
          customerCode={customerDetails.customer_code}
          settings={settings}
          isEtaxShop={isEtaxShop}
          onClose={() => setModalOpen(false)}
          onSaved={loadSettings}
        />
      )}
    </Paper>
  );
}
